/**
 * Yield to main thread.
 * Chrome 129 (https://chromestatus.com/feature/6266249336586240)
 */
export function yieldToMain(): Promise<void> {
  if ('scheduler' in window && 'yield' in (window as any).scheduler) {
    // scheduler.yield is supported
    return scheduler.yield();
  }
  return tick();
}

/**
 * Wait until the page is visually stable and the browser is idle.
 * First waits 10 animation frames (~166ms @ 60fps) to ensure paint has
 * settled, then yields to requestIdleCallback for true idle time.
 */
export async function idleTick(): Promise<void> {
  await paintTicks(10);
  if ('requestIdleCallback' in window) {
    return new Promise((resolve) => requestIdleCallback(() => resolve(), { timeout: 2_000 }));
  }
  // Fallback for Safari (no requestIdleCallback): already waited 10 frames.
  return;
}

/**
 * Wait until next repaint.
 */
export function paintTick(): Promise<number> {
  return new Promise(requestAnimationFrame);
}

/**
 * Wait for n paint ticks (aka animation frames).
 */
export async function paintTicks(n: number): Promise<void> {
  for (let i = 0; i < n; i++) {
    await paintTick();
  }
}

/**
 * Wait until timeout.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Wait until next event cycle.
 */
export function tick(): Promise<void> {
  return sleep(0);
}
